import React, { useState, useEffect } from 'react';
import { Users, MessageSquare, LayoutGrid, TrendingUp, Eye, Sparkles } from 'lucide-react';
import { getMetrics, getMetricsAsync, getModels, getCategories } from '../../services/adminStorage';

export default function MetricsPanel() {
  const [metrics, setMetrics] = useState(getMetrics());
  const [models, setModels] = useState(getModels());
  const [categories, setCategories] = useState(getCategories());

  useEffect(() => {
    // Busca dados em tempo real do banco de dados MySQL
    getMetricsAsync().then(data => {
      if (data) setMetrics(data);
    });

    const handleUpdate = () => {
      setMetrics(getMetrics());
      setModels(getModels());
      setCategories(getCategories());
    };
    window.addEventListener('suzume_data_updated', handleUpdate);
    return () => window.removeEventListener('suzume_data_updated', handleUpdate);
  }, []);

  const totalModels = models.length;
  const totalCategories = categories.length;
  const totalVisits = metrics.totalVisits || 0;
  const totalWaClicks = metrics.totalWhatsAppClicks || 0;

  // Cálculo de taxa de conversão (Cliques no WhatsApp / Visitas Únicas)
  const conversionRate = totalVisits > 0 ? ((totalWaClicks / totalVisits) * 100).toFixed(1) : '0.0';

  // Histórico de 14 dias com escala dinâmica
  const history = metrics.visitsHistory || [];
  const maxHistoryVal = Math.max(...history.map(item => Math.max(item.visits || 0, item.wa || 0)), 10);

  // Ranking Top 5 Modelos Mais Acessados / Convertidos
  const sortedTopModels = [...models].sort((a, b) => {
    const viewsA = (metrics.modelViews && metrics.modelViews[a.id]) || 0;
    const waA = (metrics.whatsAppClicksByModel && metrics.whatsAppClicksByModel[a.id]) || 0;
    const scoreA = viewsA + (waA * 2);

    const viewsB = (metrics.modelViews && metrics.modelViews[b.id]) || 0;
    const waB = (metrics.whatsAppClicksByModel && metrics.whatsAppClicksByModel[b.id]) || 0;
    const scoreB = viewsB + (waB * 2);

    return scoreB - scoreA;
  }).slice(0, 5);

  return (
    <div className="animate-fade-in">
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.75rem', color: 'var(--text-title)', fontWeight: 800 }}>
          Métricas &amp; Analytics
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>
          Acompanhamento em tempo real dos acessos únicos, solicitações de orçamento via WhatsApp e engajamento por modelo de landing page.
        </p>
      </div>

      {/* Grid de Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '1.25rem', marginBottom: '2.5rem' }}>
        
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.5rem', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>Total de Modelos</span>
            <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(37, 99, 235, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LayoutGrid size={20} color="var(--color-brand-primary)" />
            </div>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-title)' }}>{totalModels}</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
            distribuídos em {totalCategories} nichos
          </div>
        </div>

        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.5rem', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>Acessos Únicos</span>
            <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(6, 182, 212, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={20} color="var(--color-brand-cyan)" />
            </div>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-title)' }}>{totalVisits}</div>
          <div style={{ fontSize: '0.8rem', color: '#10b981', marginTop: '0.25rem' }}>
            sessões diárias registradas
          </div>
        </div>

        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.5rem', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>Orçamentos WhatsApp</span>
            <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(37, 211, 102, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MessageSquare size={20} color="var(--color-whatsapp)" />
            </div>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-title)' }}>{totalWaClicks}</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
            cliques nos botões de contato
          </div>
        </div>

        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.5rem', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>Taxa de Conversão</span>
            <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(245, 158, 11, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp size={20} color="var(--color-warning)" />
            </div>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-title)' }}>{conversionRate}%</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
            conversão visitas -&gt; WhatsApp
          </div>
        </div>

      </div>

      {/* Histórico Visual em Barras */}
      <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.75rem', marginBottom: '2.5rem', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h3 style={{ fontSize: '1.15rem', color: 'var(--text-title)', fontWeight: 700 }}>Histórico de Acessos &amp; Contatos (Últimos 14 dias)</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Números exatos de Acessos Únicos (Azul) e Leads no WhatsApp (Verde)</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.85rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--color-brand-primary)' }}></span> Visitas Únicas
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--color-whatsapp)' }}></span> Leads WhatsApp
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', height: '220px', padding: '1rem 0 0', borderBottom: '1px dashed var(--border-color)', overflowX: 'auto' }}>
          {history.map((item, idx) => {
            const visitsCount = item.visits || 0;
            const waCount = item.wa || 0;
            const isToday = idx === history.length - 1;

            const visitHeight = maxHistoryVal > 0 ? Math.max(visitsCount > 0 ? 12 : 3, (visitsCount / maxHistoryVal) * 85) : 3;
            const waHeight = maxHistoryVal > 0 ? Math.max(waCount > 0 ? 12 : 3, (waCount / maxHistoryVal) * 85) : 3;

            return (
              <div key={idx} style={{ flex: 1, minWidth: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                
                {/* Colunas em barras com números posicionados acima de cada uma */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', width: '100%', height: '80%', justifyContent: 'center' }}>
                  
                  {/* Coluna da Visita (Número + Barra Azul) */}
                  <div style={{ width: '42%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 800, color: visitsCount > 0 ? 'var(--color-brand-primary)' : '#94a3b8', marginBottom: '3px' }}>
                      {visitsCount}
                    </span>
                    <div 
                      style={{ 
                        width: '100%', 
                        height: `${visitHeight}%`, 
                        background: visitsCount > 0 ? 'var(--color-brand-primary)' : '#e2e8f0', 
                        borderRadius: '4px 4px 0 0',
                        transition: 'height 0.3s ease' 
                      }}
                      title={`Dia ${item.date}: ${visitsCount} acessos únicos`}
                    />
                  </div>

                  {/* Coluna do WhatsApp (Número + Barra Verde) */}
                  <div style={{ width: '42%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 800, color: waCount > 0 ? 'var(--color-whatsapp)' : '#94a3b8', marginBottom: '3px' }}>
                      {waCount}
                    </span>
                    <div 
                      style={{ 
                        width: '100%', 
                        height: `${waHeight}%`, 
                        background: waCount > 0 ? 'var(--color-whatsapp)' : '#e2e8f0', 
                        borderRadius: '4px 4px 0 0',
                        transition: 'height 0.3s ease' 
                      }}
                      title={`Dia ${item.date}: ${waCount} cliques no WhatsApp`}
                    />
                  </div>

                </div>

                {/* Rótulo de Data */}
                <span style={{ 
                  fontSize: '0.7rem', 
                  color: isToday ? 'var(--color-brand-primary)' : 'var(--text-muted)', 
                  fontWeight: isToday ? 800 : 500,
                  marginTop: '0.5rem',
                  whiteSpace: 'nowrap'
                }}>
                  {item.date}{isToday ? ' (Hoje)' : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ranking dos Top 5 Modelos Mais Acessados */}
      <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.75rem', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <div>
            <h3 style={{ fontSize: '1.15rem', color: 'var(--text-title)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Sparkles size={18} color="var(--color-brand-primary)" />
              Top 5 Modelos em Destaque no Acervo
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Classificados por engajamento de visualizações e solicitações no WhatsApp.</p>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-color)', color: 'var(--text-muted)' }}>
                <th style={{ padding: '0.75rem 1rem' }}># Top</th>
                <th style={{ padding: '0.75rem 1rem' }}>Modelo de Landing Page</th>
                <th style={{ padding: '0.75rem 1rem' }}>Nicho / Categoria</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>Visualizações</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>Cliques WhatsApp</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Status no Catálogo</th>
              </tr>
            </thead>
            <tbody>
              {sortedTopModels.map((model, index) => {
                const viewsCount = (metrics.modelViews && metrics.modelViews[model.id]) || 0;
                const waClicksCount = (metrics.whatsAppClicksByModel && metrics.whatsAppClicksByModel[model.id]) || 0;

                return (
                  <tr key={model.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '1rem', fontWeight: 800, color: 'var(--color-brand-primary)' }}>
                      #{index + 1}
                    </td>
                    <td style={{ padding: '1rem', fontWeight: 700, color: 'var(--text-title)' }}>
                      {model.name}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span className="badge badge-category">{model.category}</span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: 'var(--text-title)' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Eye size={14} color="var(--color-brand-primary)" />
                        {viewsCount}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 700, color: 'var(--color-whatsapp)' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        <MessageSquare size={14} color="var(--color-whatsapp)" />
                        {waClicksCount}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <span className="badge badge-success">Em alta</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
